import { useEffect, useState } from "react";

import { CrudModule } from "@/components/CrudModule";
import { useFarm } from "@/context/FarmContext";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  CloudSun,
  Info,
  Ruler,
  Upload,
  FileText,
  Sprout,
  TrendingUp,
  MousePointer2,
  BarChart3,
} from "lucide-react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";

import {
  api,
  formatApiErrorDetail,
} from "@/lib/api";

import { toast } from "sonner";


const BASIS = {
  total_cover: {
    residual: 1500,
    min: 2800,
    max: 3000,
    label: "Total cover (to ground)",
  },

  above_4cm: {
    residual: 0,
    min: 1300,
    max: 1600,
    label: "Cover above 4 cm",
  },
};


function statusClass(status) {
  if (status === "Remove for silage") {
    return "bg-blue-100 text-blue-800";
  }

  if (status === "Graze now") {
    return "bg-green-100 text-green-800";
  }

  if (status === "Building") {
    return "bg-amber-100 text-amber-800";
  }

  if (status === "Below residual") {
    return "bg-red-100 text-red-800";
  }

  return "bg-gray-100 text-gray-800";
}


function numberValue(value, decimals = 0) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return number.toLocaleString(
    undefined,
    {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }
  );
}


function chartColour(
  cover,
  residual,
  preGrazingMin,
  preGrazingMax
) {
  const value = Number(cover) || 0;

  if (
    value <=
    Number(residual || 0)
  ) {
    return "hsl(0,61%,46%)";
  }

  if (
    value >
    Number(preGrazingMax || 0)
  ) {
    return "hsl(210,60%,50%)";
  }

  if (
    value >=
    Number(preGrazingMin || 0)
  ) {
    return "hsl(145,47%,35%)";
  }

  return "hsl(173,58%,39%)";
}


function buildChartData(items) {
  return (items || [])
    .map((paddock) => {
      const cover =
        Number(
          paddock.projected_cover ??
            paddock.current_cover
        ) || 0;

      return {
        id: paddock.id,

        name:
          paddock.name ||
          "Unnamed",

        shortName:
          paddock.name &&
          paddock.name.length > 14
            ? `${paddock.name.slice(
                0,
                13
              )}…`
            : paddock.name ||
              "Unnamed",

        compactName:
          paddock.name &&
          paddock.name.length > 8
            ? `${paddock.name.slice(
                0,
                7
              )}…`
            : paddock.name ||
              "Unnamed",

        cover,

        measuredCover:
          Number(
            paddock.current_cover
          ) || 0,

        area:
          Number(
            paddock.area
          ) || 0,

        growth:
          Number(
            paddock.growth_rate_used ??
              paddock.growth_rate
          ) || 0,

        status:
          paddock.status ||
          "Unknown",
      };
    })
    .sort(
      (a, b) =>
        b.cover - a.cover
    );
}


function WedgeLegend() {
  return (
    <div className="flex flex-wrap gap-2 text-[10px] sm:text-xs">

      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1">
        <span className="h-2.5 w-2.5 rounded-sm bg-[hsl(210,60%,50%)]" />
        Above target
      </span>

      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1">
        <span className="h-2.5 w-2.5 rounded-sm bg-[hsl(145,47%,35%)]" />
        Graze range
      </span>

      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1">
        <span className="h-2.5 w-2.5 rounded-sm bg-[hsl(173,58%,39%)]" />
        Building
      </span>

      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1">
        <span className="h-2.5 w-2.5 rounded-sm bg-[hsl(0,61%,46%)]" />
        Below residual
      </span>

    </div>
  );
}


function WedgeReferenceLines({
  residual,
  preGrazingMin,
  preGrazingMax,
}) {
  return (
    <>
      {Number(preGrazingMax) > 0 && (
        <ReferenceLine
          y={Number(
            preGrazingMax
          )}
          stroke="hsl(210,60%,50%)"
          strokeDasharray="5 4"
        />
      )}

      {Number(preGrazingMin) > 0 && (
        <ReferenceLine
          y={Number(
            preGrazingMin
          )}
          stroke="hsl(145,47%,35%)"
          strokeDasharray="5 4"
        />
      )}

      {Number(residual) > 0 && (
        <ReferenceLine
          y={Number(
            residual
          )}
          stroke="hsl(0,61%,46%)"
          strokeDasharray="3 3"
        />
      )}
    </>
  );
}


function InteractiveGrassWedge({
  chartData,
  residual,
  preGrazingMin,
  preGrazingMax,
}) {
  const chartWidth = Math.max(
    760,
    chartData.length * 64
  );

  return (
    <>
      <div className="sm:hidden flex items-center gap-2 text-xs text-muted-foreground mb-3">
        <TrendingUp className="h-4 w-4 text-primary" />
        Swipe sideways to view all paddocks
      </div>

      <div className="w-full overflow-x-auto overscroll-x-contain">

        <div
          style={{
            width: `${chartWidth}px`,
            height: "360px",
          }}
        >

          <ResponsiveContainer
            width="100%"
            height="100%"
          >

            <BarChart
              data={chartData}
              margin={{
                top: 15,
                right: 20,
                left: 0,
                bottom: 70,
              }}
            >

              <XAxis
                dataKey="shortName"
                angle={-40}
                textAnchor="end"
                interval={0}
                height={85}
                tick={{
                  fontSize: 10,
                  fill: "#4A5568",
                }}
              />

              <YAxis
                width={58}
                tick={{
                  fontSize: 10,
                  fill: "#4A5568",
                }}
              />

              <Tooltip
                cursor={{
                  fill:
                    "hsl(138,45%,96%)",
                }}
                content={({
                  active,
                  payload,
                }) => {
                  if (
                    !active ||
                    !payload?.length
                  ) {
                    return null;
                  }

                  const row =
                    payload[0].payload;

                  return (
                    <div className="rounded-lg border bg-white p-3 shadow-lg text-xs">

                      <p className="font-semibold text-[hsl(148,38%,16%)] mb-2">
                        {row.name}
                      </p>

                      <div className="space-y-1 text-muted-foreground">

                        <p>
                          Cover today:{" "}
                          <strong className="text-foreground">
                            {numberValue(
                              row.cover
                            )}{" "}
                            kg DM/ha
                          </strong>
                        </p>

                        <p>
                          Measured cover:{" "}
                          <strong className="text-foreground">
                            {numberValue(
                              row.measuredCover
                            )}{" "}
                            kg DM/ha
                          </strong>
                        </p>

                        <p>
                          Area:{" "}
                          <strong className="text-foreground">
                            {numberValue(
                              row.area,
                              2
                            )}{" "}
                            ha
                          </strong>
                        </p>

                        <p>
                          Growth:{" "}
                          <strong className="text-foreground">
                            {numberValue(
                              row.growth,
                              1
                            )}{" "}
                            kg DM/ha/day
                          </strong>
                        </p>

                        <p>
                          Status:{" "}
                          <strong className="text-foreground">
                            {row.status}
                          </strong>
                        </p>

                      </div>

                    </div>
                  );
                }}
              />

              <WedgeReferenceLines
                residual={residual}
                preGrazingMin={
                  preGrazingMin
                }
                preGrazingMax={
                  preGrazingMax
                }
              />

              <Bar
                dataKey="cover"
                radius={[
                  5,
                  5,
                  0,
                  0,
                ]}
              >

                {chartData.map(
                  (paddock) => (
                    <Cell
                      key={
                        paddock.id ||
                        paddock.name
                      }
                      fill={chartColour(
                        paddock.cover,
                        residual,
                        preGrazingMin,
                        preGrazingMax
                      )}
                    />
                  )
                )}

              </Bar>

            </BarChart>

          </ResponsiveContainer>

        </div>

      </div>
    </>
  );
}


function StaticGrassWedge({
  chartData,
  residual,
  preGrazingMin,
  preGrazingMax,
}) {
  /*
   * This chart deliberately has no Tooltip
   * and no horizontal scroll container.
   *
   * Every paddock is compressed into the
   * available width so the entire grass
   * wedge can be viewed in one chart.
   */

  const count =
    chartData.length;

  const fontSize =
    count > 35
      ? 7
      : count > 25
        ? 8
        : count > 15
          ? 9
          : 10;

  const angle =
    count > 15
      ? -60
      : -45;

  return (
    <div
      className="w-full"
      data-testid="static-grass-wedge"
    >

      <div className="rounded-xl bg-secondary/30 p-2 sm:p-3">

        <div className="h-[330px] sm:h-[390px] lg:h-[440px] w-full">

          <ResponsiveContainer
            width="100%"
            height="100%"
          >

            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 10,
                left: -10,
                bottom:
                  count > 20
                    ? 90
                    : 75,
              }}
              barCategoryGap={
                count > 30
                  ? "8%"
                  : "15%"
              }
            >

              <XAxis
                dataKey={
                  count > 25
                    ? "compactName"
                    : "shortName"
                }
                angle={angle}
                textAnchor="end"
                interval={0}
                height={
                  count > 20
                    ? 105
                    : 90
                }
                tick={{
                  fontSize,
                  fill: "#4A5568",
                }}
                tickLine={false}
              />

              <YAxis
                width={55}
                tick={{
                  fontSize: 9,
                  fill: "#4A5568",
                }}
                tickLine={false}
                axisLine={false}
              />

              <WedgeReferenceLines
                residual={residual}
                preGrazingMin={
                  preGrazingMin
                }
                preGrazingMax={
                  preGrazingMax
                }
              />

              <Bar
                dataKey="cover"
                isAnimationActive={
                  false
                }
                radius={[
                  3,
                  3,
                  0,
                  0,
                ]}
              >

                {chartData.map(
                  (paddock) => (
                    <Cell
                      key={
                        paddock.id ||
                        paddock.name
                      }
                      fill={chartColour(
                        paddock.cover,
                        residual,
                        preGrazingMin,
                        preGrazingMax
                      )}
                    />
                  )
                )}

              </Bar>

            </BarChart>

          </ResponsiveContainer>

        </div>

      </div>

      <p className="text-[11px] sm:text-xs text-muted-foreground text-center mt-3">
        All {chartData.length} paddocks shown in one chart · highest cover to lowest cover
      </p>

    </div>
  );
}


function FullGrassWedge({
  items,
  residual,
  preGrazingMin,
  preGrazingMax,
}) {
  const chartData =
    buildChartData(
      items
    );

  const [
    viewMode,
    setViewMode,
  ] = useState(() => {
    return (
      localStorage.getItem(
        "fh_grass_wedge_view"
      ) ||
      "interactive"
    );
  });


  useEffect(() => {
    localStorage.setItem(
      "fh_grass_wedge_view",
      viewMode
    );
  }, [viewMode]);


  if (
    chartData.length === 0
  ) {
    return (
      <Card
        className="p-5 sm:p-6 mb-6"
        data-testid="full-grass-wedge"
      >

        <div className="flex items-center gap-2">

          <Sprout className="h-5 w-5 text-primary" />

          <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
            Full Grass Wedge
          </h2>

        </div>

        <div className="py-10 text-center">

          <Sprout className="h-9 w-9 mx-auto text-muted-foreground/40 mb-3" />

          <p className="font-semibold text-[hsl(148,38%,16%)]">
            No paddock data yet
          </p>

          <p className="text-sm text-muted-foreground mt-1">
            Add paddocks or import a PastureBase report to generate the grass wedge.
          </p>

        </div>

      </Card>
    );
  }


  return (
    <Card
      className="p-4 sm:p-6 mb-6"
      data-testid="full-grass-wedge"
    >

      <div className="flex flex-col gap-4 mb-5">

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">

          <div>

            <div className="flex items-center gap-2">

              <Sprout className="h-5 w-5 text-primary" />

              <h2 className="font-display text-lg sm:text-xl font-bold text-[hsl(148,38%,16%)]">
                Full Grass Wedge
              </h2>

            </div>

            <p className="text-xs sm:text-sm text-muted-foreground mt-1">

              {viewMode ===
              "interactive"
                ? "All paddocks ranked by cover with detailed paddock information."
                : "Traditional full wedge showing every paddock in a single chart."}

            </p>

          </div>


          {/* View selector */}

          <div
            className="inline-flex self-start rounded-full bg-secondary p-1"
            data-testid="grass-wedge-view-toggle"
          >

            <button
              type="button"
              onClick={() =>
                setViewMode(
                  "interactive"
                )
              }
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-all ${
                viewMode ===
                "interactive"
                  ? "bg-white text-[hsl(148,38%,16%)] shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >

              <MousePointer2 className="h-3.5 w-3.5" />

              Interactive

            </button>


            <button
              type="button"
              onClick={() =>
                setViewMode(
                  "full"
                )
              }
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-all ${
                viewMode ===
                "full"
                  ? "bg-white text-[hsl(148,38%,16%)] shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >

              <BarChart3 className="h-3.5 w-3.5" />

              Full Chart

            </button>

          </div>

        </div>


        <WedgeLegend />

      </div>


      {viewMode ===
      "interactive" ? (

        <InteractiveGrassWedge
          chartData={
            chartData
          }
          residual={
            residual
          }
          preGrazingMin={
            preGrazingMin
          }
          preGrazingMax={
            preGrazingMax
          }
        />

      ) : (

        <StaticGrassWedge
          chartData={
            chartData
          }
          residual={
            residual
          }
          preGrazingMin={
            preGrazingMin
          }
          preGrazingMax={
            preGrazingMax
          }
        />

      )}


      <div className="border-t mt-3 pt-4">

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm">

          <div>

            <p className="text-muted-foreground">
              Paddocks
            </p>

            <p className="font-semibold text-[hsl(148,38%,16%)] mt-0.5">
              {chartData.length}
            </p>

          </div>


          <div>

            <p className="text-muted-foreground">
              Pre-grazing target
            </p>

            <p className="font-semibold text-[hsl(148,38%,16%)] mt-0.5">

              {numberValue(
                preGrazingMin
              )}
              {"–"}
              {numberValue(
                preGrazingMax
              )}{" "}
              kg DM/ha

            </p>

          </div>


          <div>

            <p className="text-muted-foreground">
              Post-grazing residual
            </p>

            <p className="font-semibold text-[hsl(148,38%,16%)] mt-0.5">

              {numberValue(
                residual
              )}{" "}
              kg DM/ha

            </p>

          </div>

        </div>

      </div>

    </Card>
  );
}


export default function Paddocks() {
  const {
    farm,
    farmId,
  } = useFarm();


  const [
    importBusy,
    setImportBusy,
  ] = useState(false);


  const [
    commitBusy,
    setCommitBusy,
  ] = useState(false);


  const [
    importResult,
    setImportResult,
  ] = useState(null);


  const basisKey =
    farm?.grass_basis ||
    "total_cover";


  const basis =
    BASIS[basisKey] ||
    BASIS.total_cover;


  const residual =
    farm?.grass_residual ??
    basis.residual;


  const preGrazingMin =
    farm?.pre_grazing_min ??
    basis.min;


  const preGrazingMax =
    farm?.pre_grazing_max ??
    basis.max;


  const analysePastureBase =
    async (event) => {
      const file =
        event.target.files?.[0];


      if (!file) {
        return;
      }


      if (!farmId) {
        toast.error(
          "Please select a farm first."
        );

        event.target.value =
          "";

        return;
      }


      if (
        !file.name
          .toLowerCase()
          .endsWith(".pdf")
      ) {
        toast.error(
          "Please choose a PDF file."
        );

        event.target.value =
          "";

        return;
      }


      const formData =
        new FormData();

      formData.append(
        "file",
        file
      );


      setImportBusy(
        true
      );

      setImportResult(
        null
      );


      try {
        const response =
          await api.post(
            `/farms/${farmId}/imports/pasturebase/analyse`,
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );


        setImportResult(
          response.data
        );


        toast.success(
          `${response.data.paddock_count || 0} paddocks detected`
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setImportBusy(
          false
        );

        event.target.value =
          "";
      }
    };


  const commitPastureBase =
    async (invalidate) => {
      const paddocks =
        importResult?.paddocks ||
        [];


      if (
        paddocks.length === 0
      ) {
        toast.error(
          "No paddocks were detected."
        );

        return;
      }


      setCommitBusy(
        true
      );


      try {
        const response =
          await api.post(
            `/farms/${farmId}/imports/pasturebase/commit`,
            {
              paddocks,

              report_type:
                importResult.report_type,

              report_year:
                importResult.report_year,

              farm_name:
                importResult.farm_name,

              herd_code:
                importResult.herd_code,

              source_filename:
                importResult.filename,
            }
          );


        invalidate();


        setImportResult(
          (current) => ({
            ...current,

            import_summary:
              response.data,
          })
        );


        toast.success(
          response.data.message ||
            "Grass measurements updated"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setCommitBusy(
          false
        );
      }
    };


  return (
    <CrudModule
      title="Paddock & Grass Management"

      subtitle={
        "Record measured covers and paddock areas. " +
        "Current cover is projected from the last measurement, " +
        "and the seven-day projection uses weather-predicted grass growth."
      }

      beforeTable={(
        items,
        invalidate
      ) => (
        <>

          <FullGrassWedge
            items={items}
            residual={
              residual
            }
            preGrazingMin={
              preGrazingMin
            }
            preGrazingMax={
              preGrazingMax
            }
          />


          <Card className="p-4 sm:p-5 mb-6">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

              <div>

                <div className="flex items-center gap-2">

                  <FileText className="h-5 w-5 text-primary" />

                  <h2 className="font-display font-bold text-[hsl(148,38%,16%)]">
                    Update from PastureBase
                  </h2>

                </div>


                <p className="text-sm text-muted-foreground mt-1">
                  Upload the latest PastureBase weekly grass report to update paddock covers, growth rates and measurement dates.
                </p>

              </div>


              <div>

                <Input
                  id="pasturebase-weekly-upload"
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={
                    analysePastureBase
                  }
                  disabled={
                    importBusy
                  }
                />


                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  disabled={
                    importBusy
                  }
                  onClick={() =>
                    document
                      .getElementById(
                        "pasturebase-weekly-upload"
                      )
                      ?.click()
                  }
                >

                  <Upload className="h-4 w-4 mr-2" />

                  {importBusy
                    ? "Analysing..."
                    : "Upload weekly report"}

                </Button>

              </div>

            </div>


            {importResult && (

              <div className="mt-5 rounded-lg border p-4">

                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">

                  <div>

                    <h3 className="font-semibold">
                      Weekly report ready
                    </h3>

                    <p className="text-sm text-muted-foreground mt-1">
                      Review the measurements before updating the grass table.
                    </p>

                  </div>


                  <span className="rounded-full bg-secondary px-3 py-1 text-sm font-semibold whitespace-nowrap">

                    {importResult.paddock_count ||
                      0}{" "}
                    paddocks detected

                  </span>

                </div>


                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm">

                  <div>

                    <span className="text-muted-foreground">
                      Cover date:
                    </span>{" "}

                    {importResult.measurement_date ||
                      "Not detected"}

                  </div>


                  <div>

                    <span className="text-muted-foreground">
                      Growth:
                    </span>{" "}

                    {importResult.weekly_summary
                      ?.growth_kg_dm_ha_day ??
                      "—"}{" "}

                    kg DM/ha/day

                  </div>


                  <div>

                    <span className="text-muted-foreground">
                      Cover/LU:
                    </span>{" "}

                    {importResult.weekly_summary
                      ?.cover_per_lu_kg_dm ??
                      "—"}{" "}

                    kg DM/LU

                  </div>

                </div>


                {importResult.paddocks
                  ?.length > 0 && (

                  <>

                    <div className="mt-4 overflow-hidden rounded-md border">

                      <div className="max-h-80 overflow-auto">

                        <table className="min-w-[620px] w-full text-sm">

                          <thead className="sticky top-0 bg-muted">

                            <tr className="border-b">

                              <th className="px-3 py-2 text-left">
                                Paddock
                              </th>

                              <th className="px-3 py-2 text-right">
                                Area
                              </th>

                              <th className="px-3 py-2 text-right">
                                Cover
                              </th>

                              <th className="px-3 py-2 text-right">
                                Growth
                              </th>

                            </tr>

                          </thead>


                          <tbody>

                            {importResult.paddocks.map(
                              (
                                paddock,
                                index
                              ) => (

                                <tr
                                  key={`${paddock.name}-${index}`}
                                  className="border-b last:border-b-0"
                                >

                                  <td className="px-3 py-2">
                                    {paddock.name}
                                  </td>


                                  <td className="px-3 py-2 text-right">

                                    {Number(
                                      paddock.area ||
                                        0
                                    ).toFixed(
                                      2
                                    )}

                                  </td>


                                  <td className="px-3 py-2 text-right">

                                    {paddock.weekly_data
                                      ?.has_live_cover
                                      ? Number(
                                          paddock.current_cover ||
                                            0
                                        ).toLocaleString()
                                      : "Not updated"}

                                  </td>


                                  <td className="px-3 py-2 text-right">

                                    {paddock.weekly_data
                                      ?.has_live_cover
                                      ? Number(
                                          paddock.growth_rate ||
                                            0
                                        ).toFixed(
                                          1
                                        )
                                      : "—"}

                                  </td>

                                </tr>

                              )
                            )}

                          </tbody>

                        </table>

                      </div>

                    </div>


                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                      <p className="text-xs text-muted-foreground">

                        Existing paddocks will be matched by name. New paddocks will be added. Paddocks without a valid live cover will keep their current Farm Hand value.

                      </p>


                      <Button
                        type="button"
                        className="rounded-full shrink-0"
                        disabled={
                          commitBusy
                        }
                        onClick={() =>
                          commitPastureBase(
                            invalidate
                          )
                        }
                      >

                        <Upload className="h-4 w-4 mr-2" />

                        {commitBusy
                          ? "Updating..."
                          : `Update ${importResult.paddocks.length} paddocks`}

                      </Button>

                    </div>

                  </>

                )}


                {importResult.import_summary && (

                  <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">

                    <p className="font-semibold">
                      Grass table updated
                    </p>

                    <p className="mt-1">

                      {importResult.import_summary.created}{" "}
                      new paddocks added and{" "}

                      {importResult.import_summary.updated}{" "}
                      existing paddocks updated.

                    </p>

                  </div>

                )}

              </div>

            )}

          </Card>

        </>
      )}

      resource="paddocks"
      testid="paddock"

      defaults={{
        name: "",
        area: 0,
        current_cover: 0,
        growth_rate: 0,
        measurement_date: "",
        last_grazed: "",
      }}

      fields={[
        {
          key: "name",
          label: "Name",
          type: "text",
          full: true,
        },

        {
          key: "area",
          label: "Area (ha)",
          type: "number",
          numeric: true,
        },

        {
          key: "current_cover",
          label: "Measured cover (kg DM/ha)",
          type: "number",
          numeric: true,
        },

        {
          key: "measurement_date",
          label: "Measured on",
          type: "date",
        },

        {
          key: "growth_rate",
          label: "Fallback growth (kg DM/ha/day)",
          type: "number",
          numeric: true,
        },

        {
          key: "growth_rate_used",
          label: "Growth used today",
          numeric: true,

          render: (paddock) =>
            `${numberValue(
              paddock.growth_rate_used,
              1
            )} kg DM/ha/day`,
        },

        {
          key: "growth_source",
          label: "Growth source",

          render: (paddock) => {
            const usesWeather =
              paddock.growth_source ===
              "weather";

            return (
              <span className="inline-flex items-center gap-1.5">

                {usesWeather && (
                  <CloudSun className="h-4 w-4 text-primary" />
                )}

                {usesWeather
                  ? "Weather forecast"
                  : "Manual paddock rate"}

              </span>
            );
          },
        },

        {
          key: "projected_cover",
          label: "Cover today",
          numeric: true,

          render: (paddock) =>
            numberValue(
              paddock.projected_cover,
              0
            ),
        },

        {
          key: "projected_cover_7d",
          label: "7-day cover",
          numeric: true,

          render: (paddock) =>
            numberValue(
              paddock.projected_cover_7d,
              0
            ),
        },

        {
          key: "available_grass_kg_dm",
          label: "Available grass (kg DM)",
          numeric: true,

          render: (paddock) =>
            numberValue(
              paddock.available_grass_kg_dm,
              0
            ),
        },

        {
          key: "status",
          label: "Status",

          render: (paddock) => {
            const status =
              paddock.status ||
              "Unknown";

            return (
              <span
                className={
                  `px-2 py-1 rounded-full text-xs ` +
                  `font-semibold ${statusClass(
                    status
                  )}`
                }
                data-testid={`paddock-status-${paddock.id}`}
              >
                {status}
              </span>
            );
          },
        },

        {
          key: "last_grazed",
          label: "Last grazed",
          type: "date",
        },
      ]}

      rowExtras={() => (
        <>

          <Card
            className="mt-6 p-5"
            data-testid="grass-integration-explainer"
          >

            <div className="flex items-center gap-2 mb-2">

              <CloudSun className="h-4 w-4 text-primary" />

              <h3 className="font-display font-bold text-[hsl(148,38%,16%)]">
                Live weather integration
              </h3>

            </div>


            <p className="text-sm text-muted-foreground leading-relaxed">

              The last measured cover remains the paddock baseline. The fallback growth rate projects that measurement up to today. The seven-day cover then uses the weather-generated grass-growth forecast. Recording a new measurement or grazing event resets the baseline and prevents growth being counted twice.

            </p>

          </Card>


          <Card
            className="mt-4 p-5"
            data-testid="grass-basis-explainer"
          >

            <div className="flex items-center gap-2 mb-2">

              <Ruler className="h-4 w-4 text-primary" />

              <h3 className="font-display font-bold text-[hsl(148,38%,16%)]">
                Measurement basis: {basis.label}
              </h3>

            </div>


            <div className="flex flex-wrap gap-2 mb-3">

              <span className="text-xs rounded-full bg-secondary px-2.5 py-1">

                Residual subtracted:{" "}

                <strong>
                  {numberValue(
                    residual
                  )}{" "}
                  kg DM/ha
                </strong>

              </span>


              <span className="text-xs rounded-full bg-secondary px-2.5 py-1">

                Pre-grazing target:{" "}

                <strong>

                  {numberValue(
                    preGrazingMin
                  )}

                  {"–"}

                  {numberValue(
                    preGrazingMax
                  )}

                  {" kg DM/ha"}

                </strong>

              </span>

            </div>


            <p className="text-sm text-muted-foreground flex items-start gap-2 leading-relaxed">

              <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />

              <span>

                {basisKey ===
                "above_4cm"
                  ? (
                      "Grass is measured above 4 cm, so no residual " +
                      "is subtracted. The full measured cover is " +
                      "treated as available grass."
                    )
                  : (
                      "Grass is measured to ground, so the selected " +
                      "post-grazing residual is subtracted when " +
                      "calculating available pasture."
                    )}

                {" "}

                Change the measurement basis, residual and pre-grazing targets in Farm Management. The two measurement systems are never mixed.

              </span>

            </p>

          </Card>

        </>
      )}
    />
  );
}